/**
 * Primary top-nav links — single source of truth for both the desktop nav bar
 * (top-bar.tsx) and the mobile slide-out (mobile-nav.tsx).
 *
 * Issue #1011: the discovery/info nav is logo · location selector ·
 * Find Camps · For Providers · About · account avatar. Launch remains
 * camp-first while year-round offering pages live in contextual surfaces.
 */

export interface PrimaryNavLink {
  label: string;
  href: string;
  /** Match strategy for the active-page highlight. */
  match: "prefix" | "exact";
  /** Sub-paths that must NOT activate this link even under a prefix match — e.g.
   *  the authenticated provider portal under the marketing `/providers` route. */
  excludePrefixes?: string[];
}

/**
 * Ordered left-to-right primary nav links (excludes the logo, location selector,
 * and account avatar, which the shell renders directly).
 */
export function primaryNavLinks(): PrimaryNavLink[] {
  return [
    { label: "Find Camps", href: "/camps", match: "prefix" },
    // The authenticated provider portal lives under /providers/dashboard; a
    // provider working there must not see the marketing link highlighted.
    { label: "For Providers", href: "/providers", match: "prefix", excludePrefixes: ["/providers/dashboard"] },
    { label: "About", href: "/about", match: "exact" },
  ];
}

/**
 * Whether a nav link should render its active-page highlight for the given
 * pathname. Location-prefixed finder routes (e.g. `/bend-or/camps`) count as
 * active for the `/camps` link, so we match on the trailing segment for
 * prefix-style links rather than a literal startsWith.
 */
export function isNavLinkActive(href: string, pathname: string): boolean {
  if (!pathname) return false;
  const link = primaryNavLinks().find((l) => l.href === href);
  const match = link?.match ?? "prefix";

  // Excluded sub-paths (e.g. the provider dashboard) never activate the link,
  // even though they'd otherwise satisfy the prefix match below.
  if (
    link?.excludePrefixes?.some(
      (p) => pathname === p || pathname.startsWith(`${p}/`),
    )
  ) {
    return false;
  }

  if (match === "exact") {
    return pathname === href;
  }

  // prefix: active when the pathname is the href, a sub-path of it, or a
  // location-prefixed variant (e.g. /bend-or/camps -> /camps).
  const segment = href.replace(/^\//, ""); // "camps", "providers"
  if (pathname === href || pathname.startsWith(`${href}/`)) return true;
  // `/{location}/camps` or `/{location}/camps/...`
  return new RegExp(`^/[^/]+/${segment}(?:/|$)`).test(pathname);
}
